import { useEffect, useMemo, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { LegacyBridgeTransitionPage } from "../../../shared/custom-ui";
import {
  buildCustomizationWorkbenchEntryPath,
  CustomizationNewProjectTraceDraft,
  CustomizationWorkbenchFeaturePage,
  importLegacyCustomizationFromRoute,
  isCustomizationMobileDevice,
  parseLegacyJdReference,
} from "..";

type Props = {
  projectId?: string;
};

const legacyCustomizeRouteImportPromises = new Map<
  string,
  ReturnType<typeof importLegacyCustomizationFromRoute>
>();

/** 定制项目工作台页面入口。 */
export function CustomizationWorkbenchPage({ projectId }: Props) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const routeProjectId =
    projectId ??
    params.projectId ??
    (isNewCustomizationWorkbenchPath(location.pathname) ? "new" : "project-demo");
  const legacyRouteProject = parseLegacyJiDiaoRouteProject(routeProjectId);
  const resolvedProjectId = legacyRouteProject?.projectId ?? routeProjectId;
  const legacyQueryJiDiaoNo = searchParams.get("JiDiaoNo")?.trim() || undefined;
  const opportunityId = parseOpportunityId(
    searchParams.get("opportunityId") ?? params.opportunityId ?? null,
  );
  const legacyJiDiaoNo = legacyQueryJiDiaoNo ?? legacyRouteProject?.legacyJiDiaoNo;
  const legacyCustomizeRouteImport = useMemo(
    () =>
      buildLegacyCustomizeRouteImport({
        from: searchParams.get("from"),
        routeProjectId,
        legacyQueryJiDiaoNo,
      }),
    [legacyQueryJiDiaoNo, routeProjectId, searchParams],
  );
  const [importErrorMessage, setImportErrorMessage] = useState("");
  const hideLinkedItineraryTraces = Boolean(
    legacyQueryJiDiaoNo || legacyJiDiaoNo,
  );

  useEffect(() => {
    if (!legacyCustomizeRouteImport) {
      return;
    }
    let cancelled = false;
    const importKey = buildLegacyCustomizeRouteImportKey(legacyCustomizeRouteImport);
    const importedProjectId = readImportedProjectId(importKey);
    if (importedProjectId) {
      navigate(buildWorkbenchPath(importedProjectId), { replace: true });
      return;
    }
    setImportErrorMessage("");
    const importPromise =
      legacyCustomizeRouteImportPromises.get(importKey) ??
      importLegacyCustomizationFromRoute(legacyCustomizeRouteImport);
    legacyCustomizeRouteImportPromises.set(importKey, importPromise);
    void importPromise
      .then((result) => {
        if (cancelled) {
          return;
        }
        writeImportedProjectId(importKey, result.project.id);
        navigate(buildWorkbenchPath(result.project.id), { replace: true });
      })
      .catch(() => {
        if (!cancelled) {
          setImportErrorMessage("旧定制导入失败，请返回旧系统重试。");
        }
      })
      .finally(() => {
        legacyCustomizeRouteImportPromises.delete(importKey);
      });
    return () => {
      cancelled = true;
    };
  }, [legacyCustomizeRouteImport, navigate]);

  if (legacyCustomizeRouteImport) {
    return (
      <LegacyCustomizeRouteImportPage
        importPayload={legacyCustomizeRouteImport}
        errorMessage={importErrorMessage}
      />
    );
  }
  if (resolvedProjectId === "new") {
    return (
      <CustomizationNewProjectTraceDraft
        opportunityId={opportunityId}
        onCreated={(nextProjectId) =>
          navigate(buildWorkbenchPath(nextProjectId), { replace: true })
        }
      />
    );
  }

  // 已有项目：窄屏进入 PC 工作台地址时改走手机工作台（样式与三页壳层）。
  // 旧系统 customizeRoute 导入仍先在本页完成，导入成功后再按设备跳转。
  if (!legacyCustomizeRouteImport && !opportunityId && isCustomizationMobileDevice()) {
    return (
      <Navigate
        to={buildCustomizationWorkbenchEntryPath(resolvedProjectId, {
          search: location.search,
          forceMobile: true,
        })}
        replace
      />
    );
  }

  return (
    <CustomizationWorkbenchFeaturePage
      projectId={resolvedProjectId}
      opportunityId={opportunityId}
      legacyJiDiaoNo={legacyJiDiaoNo}
      hideLinkedItineraryTraces={hideLinkedItineraryTraces}
      onProjectChanged={(nextProjectId) =>
        navigate(buildWorkbenchPath(nextProjectId), { replace: true })
      }
      onNewCustomizationCreated={(nextProjectId) =>
        navigate(buildWorkbenchPath(nextProjectId), { replace: true })
      }
    />
  );
}

/** 兼容旧系统把 JiDiaoNo 放在路径里的跳转，例如 JD202509260689_6。 */
function parseLegacyJiDiaoRouteProject(
  value: string,
): { projectId: string; legacyJiDiaoNo?: string } | null {
  const legacyReference = parseLegacyJdReference(value);
  if (!legacyReference) {
    return null;
  }
  return {
    projectId: legacyReference.batchNo,
    legacyJiDiaoNo: legacyReference.referenceKey,
  };
}

type LegacyCustomizeRouteImport = {
  legacyBatchNo: string;
  legacyJiDiaoNo: string;
};

/** 根据旧系统 customizeRoute 参数构造导入请求。 */
function buildLegacyCustomizeRouteImport({
  from,
  routeProjectId,
  legacyQueryJiDiaoNo,
}: {
  from: string | null;
  routeProjectId: string;
  legacyQueryJiDiaoNo?: string;
}): LegacyCustomizeRouteImport | null {
  if (from !== "customizeRoute") {
    return null;
  }
  const routeReference = parseLegacyJdReference(routeProjectId);
  const queryReference = parseLegacyJdReference(legacyQueryJiDiaoNo);
  const legacyBatchNo = routeReference?.batchNo ?? queryReference?.batchNo;
  const legacyJiDiaoNo = queryReference?.referenceKey ?? routeReference?.referenceKey;
  if (!legacyBatchNo || !legacyJiDiaoNo) {
    return null;
  }
  return { legacyBatchNo, legacyJiDiaoNo };
}

/** 构造旧系统导入幂等缓存键。 */
function buildLegacyCustomizeRouteImportKey(importPayload: LegacyCustomizeRouteImport): string {
  return `customization:customize-route:${importPayload.legacyBatchNo}:${importPayload.legacyJiDiaoNo}`;
}

/** 读取旧系统导入后的新项目 ID。 */
function readImportedProjectId(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** 写入旧系统导入后的新项目 ID，避免同一页面会话重复创建。 */
function writeImportedProjectId(key: string, projectId: string): void {
  try {
    window.sessionStorage.setItem(key, projectId);
  } catch {
    // sessionStorage 不可用时仍允许本次导入完成。
  }
}

/** 旧系统定制导入过渡页。 */
function LegacyCustomizeRouteImportPage({
  importPayload,
  errorMessage,
}: {
  importPayload: LegacyCustomizeRouteImport;
  errorMessage: string;
}) {
  const isError = Boolean(errorMessage);

  return (
    <LegacyBridgeTransitionPage
      ariaLabel="旧系统定制导入状态"
      className={`customization-legacy-import-page${isError ? " is-error" : ""}`}
      eyebrow="同步旧系统定制信息"
      title={isError ? "旧系统导入失败" : "正在创建新定制"}
      description={errorMessage || "正在从旧库读取项目信息及需求定制信息，请稍候。"}
      role={isError ? "alert" : "status"}
      status={isError ? "error" : "loading"}
      meta={[
        { label: "项目号", value: importPayload.legacyBatchNo },
        { label: "定制号", value: importPayload.legacyJiDiaoNo },
      ]}
    />
  );
}

/** 测试环境重置旧系统导入幂等缓存，避免用例间共享 in-flight Promise。 */
export function resetLegacyCustomizeRouteImportCacheForTests(): void {
  legacyCustomizeRouteImportPromises.clear();
}

/** 识别专用新建工作台路径，避免缺少 :projectId 时回落成 project-demo。 */
function isNewCustomizationWorkbenchPath(pathname: string): boolean {
  return (
    pathname === "/crm/customization/projects/new/workbench" ||
    pathname.endsWith("/customization/projects/new/workbench")
  );
}

/** 解析商机 ID 查询参数。 */
function parseOpportunityId(value: string | null): number | undefined {
  if (!value) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/** 构造定制工作台路径。 */
function buildWorkbenchPath(projectId: string): string {
  return buildCustomizationWorkbenchEntryPath(projectId);
}
